import { useEffect, useRef, useState } from "react";
import { CheckCircle, Music } from "lucide-react";

// ── Музыкальный пазл — хост воспроизводит отрывки ────────────────────────────

const COLORS = ["#ff3355", "#00aaff", "#3dffa0", "#ffe600"];
const LABELS = ["A", "B", "C", "D"];

function ProgressBar({ value, max, color }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ width: "100%", height: "10px", background: "#222", borderRadius: "999px", overflow: "hidden" }}>
      <div style={{
        width: `${pct}%`, height: "100%", background: color,
        borderRadius: "999px", transition: "width 1s linear",
        boxShadow: `0 0 8px ${color}88`,
      }} />
    </div>
  );
}

export default function PuzzleMusicHost({
  options, answered, result, timeLeft, hint,
  audioUrl, coverUrl, puzzleSegments,
  players, tv,
}) {
  const audioRef     = useRef(null);
  const scheduleRef  = useRef(null); // массив setTimeout id'шников
  const [currentSeg, setCurrentSeg]   = useState(null); // null | 0..3
  const [playedSegs, setPlayedSegs]   = useState(0);    // сколько отрывков уже прозвучало
  const [isPlaying,  setIsPlaying]    = useState(false);

  // Запускаем расписание когда приходит audioUrl
  useEffect(() => {
    if (!audioUrl || result) return;

    // Чистим предыдущее расписание
    (scheduleRef.current || []).forEach(clearTimeout);
    scheduleRef.current = [];

    const audio = new Audio(audioUrl);
    audio.preload = "auto";
    audioRef.current = audio;

    setCurrentSeg(null);
    setPlayedSegs(0);
    setIsPlaying(false);

    const segs = puzzleSegments || [
      { start: 0,  duration: 2 },
      { start: 6,  duration: 2 },
      { start: 12, duration: 2 },
      { start: 18, duration: 2 },
    ];

    // Строим расписание: каждый отрывок играет duration сек, потом пауза (gap - duration)
    // Отрывки через каждые 6 сек (2 играет + 4 пауза)
    const CYCLE = 6; // сек на один цикл

    segs.forEach((seg, i) => {
      const startMs = i * CYCLE * 1000;
      const stopMs  = startMs + seg.duration * 1000;

      // Старт отрывка
      const t1 = setTimeout(() => {
        const a = audioRef.current;
        if (!a) return;
        a.currentTime = seg.start;
        a.play().catch(() => {});
        setCurrentSeg(i);
        setIsPlaying(true);
      }, startMs);

      // Стоп отрывка
      const t2 = setTimeout(() => {
        const a = audioRef.current;
        if (!a) return;
        a.pause();
        setIsPlaying(false);
        setCurrentSeg(null);
        setPlayedSegs(i + 1);
      }, stopMs);

      scheduleRef.current.push(t1, t2);
    });

    return () => {
      (scheduleRef.current || []).forEach(clearTimeout);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Когда результат — останавливаем и проигрываем полный трек
  useEffect(() => {
    if (!result) return;
    (scheduleRef.current || []).forEach(clearTimeout);
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    a.play().catch(() => {});
    setIsPlaying(true);
    setCurrentSeg(null);
  }, [result]);

  // Стоп при размонтировании
  useEffect(() => () => {
    (scheduleRef.current || []).forEach(clearTimeout);
    if (audioRef.current) audioRef.current.pause();
  }, []);

  if (!options || !options.length) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="border-4 border-[#9b5de5] border-t-transparent rounded-full animate-spin"
          style={{ width: tv ? "4rem" : "3rem", height: tv ? "4rem" : "3rem" }} />
      </div>
    );
  }

  const timerColor = timeLeft > 15 ? "#3dffa0" : timeLeft > 7 ? "#ffe600" : "#ff3355";
  const totalSegs  = (puzzleSegments || []).length;

  return (
    <div className="w-full animate-slide-up">
      <div className={`flex ${tv ? "flex-row gap-8" : "flex-col gap-4"} w-full`}>

        {/* ── Левая колонка: плеер + статус ── */}
        <div className={`${tv ? "w-[44%]" : "w-full"} flex flex-col gap-3`}>

          {/* Бейдж */}
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: ".4rem",
              background: "#1a0040", border: "2.5px solid #9b5de5",
              borderRadius: "1rem", boxShadow: "3px 3px 0 #000",
              padding: tv ? ".5rem 1.1rem" : ".35rem .8rem",
            }}>
              <span style={{ fontSize: tv ? "1.3rem" : "1rem" }}>🧩</span>
              <span className="font-heading" style={{ fontSize: tv ? "1.1rem" : ".8rem", color: "#9b5de5" }}>
                Музыкальный пазл
              </span>
            </div>
            {hint && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: ".5rem",
                background: "#261d60", border: "2.5px solid #000",
                borderRadius: "1rem", boxShadow: "3px 3px 0 #000",
                padding: tv ? ".5rem 1.1rem" : ".35rem .8rem",
              }}>
                <Music style={{ color: "#9b5de5", width: tv ? "1.3rem" : "1rem", height: tv ? "1.3rem" : "1rem" }} />
                <span className="font-heading" style={{ fontSize: tv ? "1.1rem" : ".8rem", color: "#ccc" }}>{hint}</span>
              </div>
            )}
          </div>

          {/* Визуализация отрывков */}
          <div style={{
            background: "#0a0a14",
            border: `3px solid ${isPlaying ? "#9b5de5" : "#222"}`,
            borderRadius: "1.25rem",
            padding: tv ? "1.5rem" : "1rem",
            textAlign: "center",
            boxShadow: isPlaying ? "0 0 20px #9b5de544" : "none",
            transition: "border-color 0.3s, box-shadow 0.3s",
          }}>
            {/* Обложка */}
            {coverUrl && result && (
              <img src={coverUrl} alt="" style={{
                width: tv ? "100px" : "70px", height: tv ? "100px" : "70px",
                borderRadius: "0.5rem", objectFit: "cover",
                margin: "0 auto 0.75rem",
                border: "2px solid #333",
              }} />
            )}

            {/* Иконка */}
            {!result && (
              <div style={{
                fontSize: tv ? "3rem" : "2rem",
                marginBottom: "0.5rem",
                animation: isPlaying ? "float 1s ease-in-out infinite" : "none",
              }}>
                {isPlaying ? "🎵" : "⏸"}
              </div>
            )}

            {/* Статус */}
            <div className="font-heading" style={{
              fontSize: tv ? "1.1rem" : "0.8rem",
              color: isPlaying ? "#9b5de5" : "#555",
              marginBottom: "0.75rem",
            }}>
              {result
                ? "🎵 Слушайте полный трек!"
                : isPlaying
                  ? `▶ Отрывок ${(currentSeg ?? 0) + 1} из ${totalSegs}`
                  : playedSegs < totalSegs
                    ? `⏳ Следующий отрывок...`
                    : "Все отрывки сыграны!"}
            </div>

            {/* Сегменты-индикаторы */}
            <div style={{ display: "flex", gap: "0.4rem", justifyContent: "center" }}>
              {Array.from({ length: totalSegs }).map((_, i) => (
                <div key={i} style={{
                  width: tv ? "2.5rem" : "1.8rem",
                  height: tv ? "0.6rem" : "0.45rem",
                  borderRadius: "999px",
                  background: i < playedSegs
                    ? "#9b5de5"
                    : currentSeg === i
                      ? "#ffe600"
                      : "#333",
                  boxShadow: currentSeg === i ? "0 0 8px #ffe600" : "none",
                  transition: "background 0.3s",
                }} />
              ))}
            </div>
          </div>

          {/* Таймер */}
          {!result && (
            <div>
              <div className="font-heading text-center mb-1" style={{
                fontSize: tv ? "1.4rem" : "1rem", color: timerColor,
                textShadow: `0 0 12px ${timerColor}`,
                animation: timeLeft <= 7 ? "wiggle .4s ease-in-out infinite" : "none",
              }}>⏱ {timeLeft} сек</div>
              <ProgressBar value={timeLeft} max={30} color={timerColor} />
            </div>
          )}

          {/* Ответившие */}
          {!result && (
            <div>
              <div className="font-heading text-[#aaa] mb-2" style={{ fontSize: tv ? "1.1rem" : ".8rem" }}>
                Ответили: {answered.length}/{players.length}
              </div>
              <div className="flex flex-wrap gap-2">
                {players.map((p, i) => {
                  const done = answered.includes(p.name);
                  return (
                    <div key={i} className="player-chip" style={{
                      backgroundColor: p.color, opacity: done ? 1 : .3,
                      fontSize: tv ? ".95rem" : ".7rem",
                      padding: tv ? ".3rem .85rem" : ".2rem .55rem",
                      transition: "opacity .3s",
                      display: "flex", alignItems: "center", gap: ".2rem",
                    }}>
                      {done && <CheckCircle style={{ width: ".75rem", height: ".75rem" }} />}
                      {p.name}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Правая колонка: варианты ── */}
        <div className="flex-1 grid grid-cols-2 gap-3" style={{ alignContent: "start" }}>
          {options.map((opt, i) => {
            const isCorrect = result && result.correct === i;
            const isWrong   = result && result.correct !== i;
            const whoVoted  = result
              ? Object.entries(result.answers || {}).filter(([, a]) => a === i).map(([n]) => n)
              : [];
            return (
              <div key={i} style={{
                background: COLORS[i], border: "2.5px solid #000",
                borderRadius: "1.25rem",
                boxShadow: isCorrect ? "8px 8px 0 #000" : "5px 5px 0 #000",
                padding: tv ? "1.5rem 1rem" : "1rem .75rem",
                textAlign: "center",
                opacity: isWrong ? .4 : 1,
                transform: isCorrect ? "scale(1.04)" : "scale(1)",
                transition: "all .35s", position: "relative",
              }}>
                {isCorrect && (
                  <div style={{
                    position: "absolute", top: "-.6rem", right: "-.6rem",
                    background: "#fff", borderRadius: "50%",
                    width: "1.8rem", height: "1.8rem",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "2px solid #000", boxShadow: "2px 2px 0 #000",
                  }}>
                    <CheckCircle style={{ color: "#000", width: "1.2rem", height: "1.2rem" }} />
                  </div>
                )}
                <div className="font-display" style={{ fontSize: tv ? "2.5rem" : "1.4rem", color: "#000", marginBottom: ".2rem" }}>
                  {LABELS[i]}
                </div>
                <div className="font-heading" style={{ fontSize: tv ? "1.05rem" : ".72rem", color: "#000", lineHeight: 1.25 }}>
                  {opt}
                </div>
                {result && whoVoted.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1 mt-2">
                    {whoVoted.map(n => {
                      const pl = players.find(p => p.name === n);
                      return (
                        <span key={n} style={{
                          background: pl?.color || "#fff", border: "1.5px solid #000",
                          borderRadius: "99px", padding: ".1rem .5rem",
                          fontSize: tv ? ".8rem" : ".6rem",
                          fontFamily: "'Fredoka One',sans-serif", color: "#000",
                        }}>{n}</span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Reveal */}
          {result && (
            <div className="col-span-2 animate-bounce-in text-center" style={{
              background: "#1e1650", border: "2.5px solid #9b5de5",
              borderRadius: "1.25rem", boxShadow: "5px 5px 0 #000",
              padding: tv ? "1.25rem" : ".75rem",
            }}>
              <div className="font-display" style={{ color: "#ffe600", fontSize: tv ? "1.5rem" : "1rem", marginBottom: ".2rem" }}>
                🎵 {result.song}
              </div>
              <div className="font-heading text-[#ccc]" style={{ fontSize: tv ? "1.1rem" : ".8rem" }}>
                {result.artist}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}